import type { TagSymbolMappingCategory } from '../../utilities/converters';
import { TAG_SYMBOL_MAPPING_CATEGORY_KEYS } from '../../utilities/tagSymbolMappings';

export const MIN_TIMEOUT_SEC = 15;

const TAG_SYMBOL_MAPPING_META: Record<
  TagSymbolMappingCategory,
  {
    label: string;
    hint: string;
  }
> = {
  skiptts: {
    label: 'Skip TTS',
    hint: 'e.g. { "from": ":umbrella:", "to": ":umbrella:" }',
  },
  ttsOnly: {
    label: 'TTS Only',
    hint: 'e.g. { "from": "♫", "to": "♫" }',
  },
  lnk: {
    label: 'Link',
    hint: 'e.g. { "from": "[$lnk", "to": "endlnk$]" }',
  },
  red: {
    label: 'Redirect link',
    hint: 'e.g. { "from": "[red", "to": "red]" }',
  },
};

export const TAG_SYMBOL_MAPPING_CATEGORIES: {
  key: TagSymbolMappingCategory;
  label: string;
  hint: string;
}[] = TAG_SYMBOL_MAPPING_CATEGORY_KEYS.map(key => ({
  key,
  ...TAG_SYMBOL_MAPPING_META[key],
}));

export const logLevelOptions = [
  { value: 'CRITICAL', label: 'Critical (for production)' },
  { value: 'ERROR', label: 'Error (for production)' },
  { value: 'WARNING', label: 'Warning (for development)' },
  { value: 'INFO', label: 'Info (for development)' },
  { value: 'DEBUG', label: 'Debug (for development)' },
];

export const CUSTOM_URL_VALUE = 'custom_url';

export const ambientSoundOptions: OptionType<string | null>[] = [
  { value: null, label: 'None' },
  { value: 'office_ambience', label: 'Office Ambience' },
  { value: 'city_ambience', label: 'City Ambience' },
  { value: 'forest_ambience', label: 'Forest Ambience' },
  { value: 'crowded_room', label: 'Crowded Room' },
  { value: 'hold_music', label: 'Hold Music' },
  { value: CUSTOM_URL_VALUE, label: 'Custom URL...' },
];

export const thinkingSoundOptions: OptionType<string | null>[] = [
  { value: null, label: 'None' },
  { value: 'keyboard_typing', label: 'Keyboard Typing' },
  { value: 'keyboard_typing2', label: 'Keyboard Typing (Variant)' },
  { value: CUSTOM_URL_VALUE, label: 'Custom URL...' },
];

export const backgroundAudioVolumeStepOptions = [
  '0',
  '0.1',
  '0.2',
  '0.3',
  '0.4',
  '0.5',
  '0.6',
  '0.7',
  '0.8',
  '0.9',
  '1.0',
];

export const TURN_DETECTOR_MODE = {
  MULTILINGUAL_MODEL: 'multilingual_model',
  AUDIO_MODEL: 'audio_model',
  VAD: 'vad',
  STT: 'stt',
  REALTIME_LLM: 'realtime_llm',
  MANUAL: 'manual',
} as const;

export const ENDPOINTING_MODE = {
  FIXED: 'fixed',
  DYNAMIC: 'dynamic',
} as const;

export const INTERRUPTION_MODE = {
  AUTO: null as null,
  ADAPTIVE: 'adaptive',
  VAD: 'vad',
} as const;

export const REALTIME_VAD_TYPE = {
  SEMANTIC_VAD: 'semantic_vad',
  SERVER_VAD: 'server_vad',
} as const;

export const REALTIME_VAD_EAGERNESS = {
  AUTO: 'auto',
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
} as const;

export const VAD_SAMPLE_RATE = {
  RATE_8K: 8000,
  RATE_16K: 16000,
} as const;

export const MODES_WITH_ENDPOINTING = [
  TURN_DETECTOR_MODE.MULTILINGUAL_MODEL,
  TURN_DETECTOR_MODE.AUDIO_MODEL,
  TURN_DETECTOR_MODE.VAD,
  TURN_DETECTOR_MODE.STT,
] as const;

export const MODES_WITH_INTERRUPTION = [
  TURN_DETECTOR_MODE.MULTILINGUAL_MODEL,
  TURN_DETECTOR_MODE.AUDIO_MODEL,
  TURN_DETECTOR_MODE.VAD,
  TURN_DETECTOR_MODE.STT,
  TURN_DETECTOR_MODE.REALTIME_LLM,
] as const;

export const MODES_WITH_VAD = [
  TURN_DETECTOR_MODE.MULTILINGUAL_MODEL,
  TURN_DETECTOR_MODE.AUDIO_MODEL,
  TURN_DETECTOR_MODE.VAD,
  TURN_DETECTOR_MODE.STT,
] as const;

export const turnDetectorModeOptions = [
  {
    value: 'multilingual_model',
    label: 'Text Turn Detector',
    description:
      'ML model that understands conversation context. Supports 14 languages. Best accuracy.',
  },
  {
    value: 'audio_model',
    label: 'Audio Turn Detector',
    description: 'Audio-based model for turn detection.',
  },
  {
    value: 'vad',
    label: 'Voice Activity Detection',
    description:
      'Detects silence gaps only. Simple but may cut off mid-sentence.',
  },
  {
    value: 'stt',
    label: 'STT Endpointing',
    description: "Uses the speech-to-text provider's built-in turn detection.",
  },
  {
    value: 'realtime_llm',
    label: 'Realtime Model',
    description:
      'Server-side detection by the realtime LLM. Auto-selected for OpenAI Realtime, Gemini Live, etc.',
  },
  {
    value: 'manual',
    label: 'Manual / Push-to-Talk',
    description:
      'No automatic detection. For push-to-talk or API-controlled scenarios.',
  },
];

export const endpointingModeOptions = [
  { value: 'fixed', label: 'Fixed', description: 'Uses static delay values.' },
  {
    value: 'dynamic',
    label: 'Dynamic',
    description: 'Adapts delays based on conversation pace.',
  },
];

export const interruptionModeOptions = [
  {
    value: null,
    label: 'Auto',
    description: 'Backend picks the best strategy.',
  },
  {
    value: 'adaptive',
    label: 'Adaptive',
    description:
      'AI distinguishes real interruptions from backchanneling (uh-huh, yeah).',
  },
  {
    value: 'vad',
    label: 'VAD',
    description: 'Any detected speech counts as an interruption.',
  },
];

export const vadSampleRateOptions = [
  { value: 8000, label: '8000 Hz' },
  { value: 16000, label: '16000 Hz' },
];

export const realtimeVadTypeOptions = [
  { value: 'semantic_vad', label: 'Semantic VAD' },
  { value: 'server_vad', label: 'Server VAD' },
];

export const realtimeVadEagernessOptions = [
  { value: 'auto', label: 'Auto' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];

export const vadActivationThresholdStepOptions = Array.from(
  { length: 18 },
  (_, i) => String(Math.round((i * 0.05 + 0.1) * 100) / 100)
);

export const USER_TURN_TEMPLATE_VARIABLE = '{user_turn}';
